import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import Sidebar from '../components/dashboard/Sidebar';
import CandidateSidebar from '../components/candidate/CandidateSidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { FiPlus, FiArrowLeft, FiSend, FiMessageSquare } from 'react-icons/fi';
import CreateTicketModal from '../components/dashboard/CreateTicketModal';
import Badge3D from '../components/ui/Badge3D';
import api from '../api/api';
import { AuthContext } from '../context/AuthContext';

// No mock data - always use real data from backend

const SupportPage = () => {
  const { user } = useContext(AuthContext);
  const isCandidate = user?.userType === 'candidate';
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const [tickets, setTickets] = useState([]);
  
  // Fetch support tickets ONCE on mount
  useEffect(() => {
    const fetchTickets = async () => {
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        
        // CRITICAL: Wait for auth to settle before fetching (prevent race condition)
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // CRITICAL: Check if user is authenticated before fetching
        if (!user || !user.id) {
          console.error('[SupportPage] Cannot fetch tickets - user not authenticated:', user);
          setError('Please wait while we verify your session...');
          setLoading(false);
          return;
        }
        
        // CRITICAL: Check if token exists before making request
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.error('[SupportPage] Cannot fetch tickets - no auth token found');
          setError('Session expired. Please login again.');
          setLoading(false);
          return;
        }
        
        console.log('[SupportPage] Fetching tickets for user:', user.id, 'Token exists:', !!token);
        setLoading(true);
        try {
          const response = await api.get('/support/tickets');
          console.log('[SupportPage] Tickets fetched successfully');
          setApiData(response.data);
          setError(null);
        }
        catch (err) {
          console.error('[SupportPage] Failed to fetch support tickets:', err);
          console.error('[SupportPage] Error status:', err.response?.status);
          console.error('[SupportPage] Error details:', err.response?.data || err.message);
          console.error('[SupportPage] Token still exists?', !!localStorage.getItem('authToken'));
          
          // Don't clear tokens on error - let the interceptor handle it
          setError(err.response?.data?.message || err.message || 'Failed to load tickets');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchTickets();
  }, [user]);
  
  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      console.log('[SupportPage] Refetching tickets...');
      const response = await api.get('/support/tickets');
      console.log('[SupportPage] Tickets refetched successfully:', response.data);
      setApiData(response.data);
      setError(null);
    } catch (err) {
      console.error('[SupportPage] Refetch failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to refresh tickets');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    // Use real data from backend API
    if (apiData) {

      // Check different response structures
      if (apiData.tickets) {
        setTickets(apiData.tickets);
      } else if (apiData.data && apiData.data.tickets) {
        setTickets(apiData.data.tickets);
      } else {
        console.warn('[WARNING] No tickets found in API response');
        setTickets([]);
      }
    } else if (!loading) {

      setTickets([]);
    }
  }, [apiData, loading]);
  
  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetail(true);
  };
  
  const handleBackToList = () => {
    setShowTicketDetail(false);
    setSelectedTicket(null);
  };

  return (
    <div className="flex bg-gray-50 min-h-screen font-sans relative">
      <div className="fixed left-0 top-0 bottom-0 z-10">
        {isCandidate ? <CandidateSidebar /> : <Sidebar />}
      </div>
      <main className="flex-1 p-8 ml-0 md:ml-24">
        <DashboardHeader />
        <div className="mt-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Help & Support</h1>
              <p className="text-gray-500 mt-1">Track your support requests and get help from our team.</p>
            </div>
            <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 bg-yellow-500 text-white font-semibold py-2 px-4 border border-yellow-600" style={{ 
              boxShadow: '2px 2px 0 rgba(0, 0, 0, 0.8)',
              transform: 'translateX(-2px) translateY(-2px)'
            }}>
              <FiPlus />
              Create New Ticket
            </button>
          </div>

          {!showTicketDetail ? (
            <div className="bg-white p-6 rounded-lg border border-gray-200" style={{ 
                boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.8)',
                transform: 'translateX(-3px) translateY(-3px)'
              }}>
              {loading || tickets.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-gray-500">
                      <tr>
                        <th className="p-3">Ticket ID</th>
                        <th className="p-3">Subject</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Last Updated</th>
                        <th className="p-3">Priority</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map(ticket => (
                        <tr 
                          key={ticket.id} 
                          className="border-t hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleTicketClick(ticket)}
                        >
                          <td className="p-3 font-medium text-yellow-600">{ticket.id}</td>
                          <td className="p-3 text-gray-800">{ticket.subject}</td>
                          <td className="p-3">
                            <Badge3D 
                              className="px-2 py-1 text-xs font-medium rounded-full text-white"
                              variant={ticket.status === 'Open' ? 'info' : 
                                      ticket.status === 'In Progress' ? 'warning' : 
                                      'light'}
                              depth={1}
                            >
                              {ticket.status}
                            </Badge3D>
                          </td>
                          <td className="p-3 text-gray-600">{ticket.lastUpdated}</td>
                          <td className="p-3 text-gray-600">{ticket.priority}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiMessageSquare size={24} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Support Tickets</h3>
                  <p className="text-gray-500 mb-6">You haven't created any support tickets yet.</p>
                  <button 
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center gap-2 bg-yellow-500 text-white font-semibold py-2 px-4 border border-yellow-600"
                    style={{ 
                      boxShadow: '2px 2px 0 rgba(0, 0, 0, 0.8)',
                      transform: 'translateX(-2px) translateY(-2px)'
                    }}
                  >
                    <FiPlus />
                    Create Your First Ticket
                  </button>
                </div>
              )}
            </div>
          ) : (
            <TicketDetailView 
              ticket={selectedTicket} 
              onBack={handleBackToList} 
              onStatusChange={(status) => {
                setTickets(tickets.map(t => 
                  t.id === selectedTicket.id ? {...t, status} : t
                ));
                setSelectedTicket({...selectedTicket, status});
              }}
            />
          )}
        </div>
        <CreateTicketModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)}
          onTicketCreated={(newTicket) => {

            // Add the new ticket to the list without waiting for refetch
            if (newTicket) {
              setTickets(prevTickets => [newTicket, ...prevTickets]);
            }
            // Also refetch to ensure we have the latest data
            refetch();
          }}
        />
      </main>
    </div>
  );
};

const TicketDetailView = ({ ticket, onBack, onStatusChange }) => {
  const { refreshTokenTimestamp } = useContext(AuthContext);
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState(ticket?.messages || []);
  const [sending, setSending] = useState(false);
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    const messageText = newMessage.trim();
    
    // Optimistically add message to UI
    const optimisticMsg = {
      sender: 'user',
      text: messageText,
      time: 'Just now'
    };
    setMessages([...messages, optimisticMsg]);
    setNewMessage('');
    setSending(true);
    
    try {
      // Send to backend
      const response = await api.post(`/support/tickets/${ticket.id}/comments`, {
        text: messageText
      });
      
      console.log('[TicketDetail] Comment added:', response.data);
      
      // Track activity timestamp
      sessionStorage.setItem('lastUserActivity', Date.now().toString());
      
      // Refresh token timestamp if available
      if (refreshTokenTimestamp) {
        refreshTokenTimestamp();
      }
      
      // Reconcile with server response if needed
      if (response.data && response.data.success && response.data.data) {
        // Could update with server timestamp/ID if needed
      }
    } catch (err) {
      console.error('[TicketDetail] Failed to send message:', err);
      // Revert optimistic update on error
      setMessages(messages);
      setNewMessage(messageText);
      alert('Failed to send message: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };
  
  const handleStatusChange = (newStatus) => {
    onStatusChange(newStatus);
  };
  
  return (
    <div className="bg-white border border-black" style={{ 
      boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.8)',
      transform: 'translateX(-3px) translateY(-3px)'
    }}>
      {/* Ticket Header */}
      <div className="p-4 border-b border-black flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h2 className="font-bold text-lg">{ticket.id}: {ticket.subject}</h2>
            <div className="flex items-center gap-3 mt-1">
              <Badge3D 
                className="px-2 py-1 text-xs font-medium rounded-full text-white"
                variant={ticket.status === 'Open' ? 'info' : 
                        ticket.status === 'In Progress' ? 'warning' : 
                        'light'}
                depth={1}
              >
                {ticket.status}
              </Badge3D>
              <span className="text-sm text-gray-500">Priority: {ticket.priority}</span>
              <span className="text-sm text-gray-500">Updated: {ticket.lastUpdated}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {ticket.status !== 'Closed' && (
            <select 
              className="border border-black px-2 py-1 text-sm"
              value={ticket.status}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Closed">Closed</option>
            </select>
          )}
        </div>
      </div>
      
      {/* Messages */}
      <div className="p-6 max-h-[500px] overflow-y-auto space-y-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-md p-3 ${msg.sender === 'user' ? 'bg-black text-white' : 'bg-gray-100 border border-black'}`}>
              <p className="text-sm">{msg.text}</p>
              <p className="text-xs mt-1 text-right">{msg.time}</p>
            </div>
          </div>
        ))}
      </div>
      
      {/* Message Input */}
      {ticket.status !== 'Closed' && (
        <div className="p-4 border-t border-black">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input 
              type="text" 
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message here..."
              className="flex-grow border border-black p-2"
            />
            <button 
              type="submit" 
              disabled={!newMessage.trim()}
              className="bg-black text-white px-4 py-2 flex items-center gap-2 disabled:opacity-50"
            >
              <FiSend size={16} />
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
